import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  ShieldCheck,
  RefreshCw,
  Upload,
  Target,
  BarChart3,
  Activity,
  Brain,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import AadhaarExecutiveTab from "@/components/AadhaarExecutiveTab";
import AadhaarInsightsTab from "@/components/AadhaarInsightsTab";
import AadhaarOperationalTab from "@/components/AadhaarOperationalTab";
import AadhaarActionAccountabilityTab from "@/components/AadhaarActionAccountabilityTab";
import "@/components/AadhaarInsightsTab.css";
import "@/components/AadhaarExecutiveTab.css";
import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "operational", label: "Operational Control", icon: Activity },
  { id: "action", label: "Action & Accountability", icon: Target },
  { id: "insights", label: "Insights", icon: Brain },
];

const AadhaarDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [statusDistribution, setStatusDistribution] = useState(null);
  const [highRiskSchools, setHighRiskSchools] = useState([]);
  const [bottomBlocks, setBottomBlocks] = useState([]);
  const [paretoData, setParetoData] = useState([]);
  const [mbuData, setMbuData] = useState([]);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        blockRes,
        statusRes,
        highRiskRes,
        bottomRes,
        paretoRes,
        mbuRes,
      ] = await Promise.all([
        axios.get(`${API}/aadhaar/overview`),
        axios.get(`${API}/aadhaar/block-wise`),
        axios.get(`${API}/aadhaar/status-distribution`),
        axios.get(`${API}/aadhaar/high-risk-schools`),
        axios.get(`${API}/aadhaar/bottom-blocks`),
        axios.get(`${API}/aadhaar/pareto-analysis`),
        axios.get(`${API}/aadhaar/mbu-analysis`),
      ]);

      setOverview(overviewRes.data);
      setBlockData(blockRes.data);
      setStatusDistribution(statusRes.data);
      setHighRiskSchools(highRiskRes.data);
      setBottomBlocks(bottomRes.data);
      setParetoData(paretoRes.data);
      setMbuData(mbuRes.data);
    } catch (error) {
      console.error("Error fetching Aadhaar data:", error);
    } finally {
      setLoading(false);
    }
  }, [scope.version]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    const url =
      "https://customer-assets.emergentagent.com/job_maharashtra-edu/artifacts/us8jnwni_1.%20Exceptional%20Data%20-%20AADHAAR%20Status%20-%20School%20Wise%20-%202025-26%20-%20%28State%29%20MAHARASHTRA.xlsx";

    setImporting(true);
    try {
      await axios.post(`${API}/aadhaar/import?url=${encodeURIComponent(url)}`);
      toast.success("Aadhaar data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_enrolment > 0;

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const headerCopy = {
    executive: {
      eyebrow: `Executive overview · ${scopeLabel} · AY 2025–26`,
      subtitle:
        "Decision cockpit for coverage health, exception triage, block hotspots and accountable next actions.",
    },
    operational: {
      eyebrow: `Operational control · ${scopeLabel} · AY 2025–26`,
      subtitle: "School-wise exception control, mismatch risk, MBU backlog and officer accountability.",
    },
    action: {
      eyebrow: `Action & accountability · ${scopeLabel} · AY 2025–26`,
      subtitle: "Closure workstreams, sprint timeline, escalation rules and priority school queue.",
    },
    insights: {
      eyebrow: `AI insights · ${scopeLabel} · AY 2025–26`,
      subtitle:
        "Mistral-powered signals, root-cause diagnosis, recommended actions and explainable insights.",
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.executive;

  return (
    <div className="aa-dashboard space-y-6 animate-fade-in" data-testid="aadhaar-dashboard">
      <DashboardPageHeader
        title="Aadhaar Analytics Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "School-wise Aadhaar Status • AY 2025-26 • Maharashtra"
        }
        eyebrow={
          hasData ? (
            <>
              <Sparkles size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-aadhaar-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="aadhaar" dashboardTitle="Aadhaar Analytics" />
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ShieldCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Aadhaar Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Aadhaar status Excel file</p>
            <Button onClick={handleImport} disabled={importing}>
              {importing ? "Importing..." : "Import Aadhaar Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="aa-tabs-wrap" aria-label="Aadhaar dashboard sections">
            <nav className="aa-tabs">
              {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`aa-tab ${activeTab === id ? "active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  data-testid={`tab-${id}`}
                >
                  <Icon />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <TabsContent value="executive" className="mt-0">
            <AadhaarExecutiveTab
              overview={overview}
              blockData={blockData}
              statusDistribution={statusDistribution}
              paretoData={paretoData}
              onNavigateTab={setActiveTab}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="operational" className="mt-0">
            <AadhaarOperationalTab
              overview={overview}
              blockData={blockData}
              mbuData={mbuData}
              highRiskSchools={highRiskSchools}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="action" className="mt-0">
            <AadhaarActionAccountabilityTab
              overview={overview}
              blockData={blockData}
              bottomBlocks={bottomBlocks}
              highRiskSchools={highRiskSchools}
            />
          </TabsContent>

          <TabsContent value="insights" className="mt-0">
            <AadhaarInsightsTab
              overview={overview}
              blockData={blockData}
              highRiskSchools={highRiskSchools}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AadhaarDashboard;
